import {
  ChangeDetectionStrategy,
  Component,
  model,
  input,
  output,
  signal,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { LUCIDE_ICONS } from '@compsych-ui-components/shared/assets/lucide-icons.generated';

export interface SidebarItem {
  /** Stable identifier used by `activeId` and emitted by `itemClick`. */
  id: string;
  /** Text label displayed next to the icon. */
  label: string;
  /** Lucide icon name (kebab-case), e.g. `'house'` or `'folder'`. */
  icon: string;
  /** Child items — renders this item as an expandable group with a chevron toggle. Child items cannot have their own children. */
  children?: SidebarItem[];
  /** When true, renders a horizontal divider above this item in the list. */
  dividerBefore?: boolean;
  /** When true, the item is non-interactive — it cannot be clicked and receives no hover or active styling. */
  disabled?: boolean;
  /**
   * When true, clicking the item emits `itemClick` but does not highlight it
   * as active or update `activeId`. Use for action items (e.g. logout)
   * that are not navigation destinations.
   */
  nonRoute?: boolean;
}

/** Default content from the Figma (node 260:4700). */
const DEFAULT_ITEMS: SidebarItem[] = [
  { id: 'home', label: 'Home', icon: 'house' },
  { id: 'connect', label: 'Connect Me', icon: 'message-circle', dividerBefore: true },
  { id: 'guide', label: 'Guide Me', icon: 'compass' },
  { id: 'assess', label: 'Assess Me', icon: 'binoculars' },
  {
    id: 'resources',
    label: 'Resource Library',
    icon: 'folder',
    dividerBefore: true,
    children: [
      { id: 'wellness', label: 'Wellness', icon: 'leaf' },
      { id: 'relationships', label: 'Relationships', icon: 'users' },
      { id: 'work', label: 'Work & Education', icon: 'briefcase' },
      { id: 'financial', label: 'Financial', icon: 'landmark' },
      { id: 'legal', label: 'Legal', icon: 'scale' },
      { id: 'lifestyle', label: 'Lifestyle', icon: 'smile' },
      { id: 'home-auto', label: 'Home & Auto', icon: 'car' },
      { id: 'abroad', label: 'Working Abroad', icon: 'plane' },
    ],
  },
];

const DEFAULT_FOOTER: SidebarItem[] = [
  { id: 'location', label: 'Location & Language', icon: 'globe', nonRoute: true },
  { id: 'logout', label: 'Logout', icon: 'log-out', nonRoute: true },
];

/**
 * Sidebar — vertical app navigation from the ComPsych Design System.
 *
 * Figma: Core Components — base 260:4700, parts 213:1585.
 *
 * Icon + label items with default / hover / selected / focus / disabled states,
 * section dividers, expandable groups (chevron), and a pinned footer
 * (location & language, logout). Data-driven via `items` / `footerItems`,
 * defaulting to the design's content. All values are sys tokens.
 */
@Component({
  selector: 'compsych-sidebar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.css',
  host: { role: 'navigation', 'aria-label': 'Sidebar' },
})
export class SidebarComponent {
  /** Navigation items rendered in the scrollable main area. Defaults to the standard ComPsych nav set. */
  readonly items = input<SidebarItem[]>(DEFAULT_ITEMS);
  /** Items pinned to the bottom of the sidebar, outside the scroll region. Intended for utility actions such as language selection and logout. */
  readonly footerItems = input<SidebarItem[]>(DEFAULT_FOOTER);
  /** Currently active item id — supports two-way binding via `[(activeId)]`. Updated on click for route items; unchanged for `nonRoute` items. */
  readonly activeId = model<string>('home');
  /**
   * Emitted with the item's `id` when a non-disabled action (`nonRoute`) item is clicked.
   * Navigation (route) items do not emit — they update `activeId` instead (bind via `[(activeId)]`).
   * Group items (those with `children`) do not emit — they only toggle their expanded state.
   */
  readonly itemClick = output<string>();

  private readonly expanded = signal<Set<string>>(new Set());

  constructor(private readonly sanitizer: DomSanitizer) {}

  isExpanded(id: string): boolean {
    return this.expanded().has(id);
  }

  isActive(id: string): boolean {
    return this.activeId() === id;
  }

  onItemClick(item: SidebarItem): void {
    if (item.disabled) return;
    if (item.children?.length) {
      this.expanded.update((s) => {
        const next = new Set(s);
        next.has(item.id) ? next.delete(item.id) : next.add(item.id);
        return next;
      });
      return;
    }
    if (item.nonRoute) {
      this.itemClick.emit(item.id);
      return;
    }
    this.activeId.set(item.id);
  }

  /**
   * Looks up a Lucide icon by name and injects explicit width/height attributes
   * so the SVG renders at the correct size when injected via [innerHTML].
   * Angular's CSS encapsulation does not add _ngcontent attributes to dynamically
   * injected content, so explicit SVG attributes are the reliable solution.
   */
  iconSvg(name: string, size = 20, strokeWidth = 1.5): SafeHtml {
    let svg = LUCIDE_ICONS[name] ?? '';
    svg = svg.replace('<svg ', `<svg width="${size}" height="${size}" `);
    if (strokeWidth !== 1.5) {
      svg = svg.replace('stroke-width="1.5"', `stroke-width="${strokeWidth}"`);
    }
    return this.sanitizer.bypassSecurityTrustHtml(svg);
  }
}
